// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- We're extending Angular's native FormControl here.
import {FormControl, FormControlState} from '@angular/forms';
import {isFormControlState} from '@aviary/forms/form.helpers';
import {AngularAsyncValidator, AngularEmissionOptions, ExtendedAbstractControl} from '@aviary/forms/extensions/form-extensions.types';
import {AsyncValidatorRemoverResult, removeAsyncValidatorsFrom} from '@aviary/forms/extensions/form-extensions.helpers';
import {isDefined} from '@aviary/store/common/typing.helpers';

export class ExtendedFormControl<
  T = any, // eslint-disable-line @typescript-eslint/no-explicit-any -- FormControl's generic type defaults to any, so we do that here as well.
  DataType extends Record<string, string> = Record<string, never>
> extends FormControl<T | null> implements ExtendedAbstractControl {

  public shouldRender: boolean = true;
  public data: DataType = {} as DataType; // Optional data that can be attached to the control.  Similar to HTML data attributes.

  public readonly uuid: string = crypto.randomUUID();
  public initialValue: T | null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Angular's validatorOrOpts arguments are typed as any, so we maintain that type.
  constructor(formState: T | null | FormControlState<T | null>, validatorOrOpts?: any, asyncValidator?: AngularAsyncValidator | null) {
    const {validatorOrOptsWithoutAsyncValidators, addRemovedAsyncValidatorsTo}: AsyncValidatorRemoverResult =
      removeAsyncValidatorsFrom(validatorOrOpts, asyncValidator);

    super(formState, validatorOrOptsWithoutAsyncValidators);

    this.initialValue = isFormControlState(formState) ? formState.value : formState;

    addRemovedAsyncValidatorsTo(this);
  }

  public override reset(formState?: T | null | FormControlState<T | null>, options?: AngularEmissionOptions): void {
    super.reset(isDefined(formState) ? formState : this.initialValue, options);
  }

  /**
   * This should be used VERY sparingly.  It messes with the integrity of the initial form value.  But it is useful when a specific form field component needs
   * to override the field's initial value, and we want to ensure that hasChanged() is working from the expected base value (like `SelectFormFieldComponent`).
   *
   * As the name implies, it resets this control's initial value to match its current value.  It also marks the field as pristine, just for completeness.
   */
  public resetInitialValueWithCurrentValue(): void {
    this.initialValue = this.value;
    this.markAsPristine({onlySelf: true});
  }

  public valueOr(fallback: NonNullable<T>): NonNullable<T> {
    return this.value ?? fallback;
  }

  /**
   * Convenience method that calls both `patchValue()` and `markAsDirty()` for this control.  If `options` is supplied, its contents are passed along to the
   * two methods as appropriate.  Otherwise, it is treated as Angular's usual set of default options: `{ emitEvent: true, onlySelf: false }`.
   *
   * This is a common use case because explicitly marking the control as dirty is crucial to make `hasChanged()` work properly.  Angular doesn't automatically
   * consider form controls that are changed programmatically to be dirty.
   *
   * Implementation note: `markAsDirty()` is called first so that whoever is subscribing to emissions from `patchValue()` will see that dirty state
   * immediately.  (This is moot but harmless when `options.emitEvent` is `false`.)
   */
  public patchValueAndMarkAsDirty(value: T | null, options?: AngularEmissionOptions): void {
    this.markAsDirty(options?.onlySelf ? {onlySelf: true} : undefined); // markAsDirty doesn't expect the emitEvent option, so we extract just onlySelf.
    this.patchValue(value, options);
  }
}
